import useTranslation from 'next-translate/useTranslation';

import { classNames } from '@/utils';

// Components
import Icon from '@/components/base/Icon';

const deprecatedConfigVariant = {
    icon: 'dot',
    className:
        'bg-icoGray-100 dark:bg-icoDarkMode-anthracite text-icoGray-500 dark:text-icoDarkMode-wolf border-icoGray-300 dark:border-icoDarkMode-beton',
    classNameShorten:
        'bg-icoGray-100 text-icoGray-500 border-icoGray-300 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf'
};

const variantConfig = {
    beta: {
        icon: 'dot',
        text: 'commons:beta',
        className: 'bg-icoOrange-100 dark:bg-icoOrange/20 text-icoOrange border-icoOrange',
        classNameShorten: 'bg-icoOrange text-white border-icoOrange',
        dataTestId: 'badge-beta'
    },
    'coming-soon': {
        icon: 'soon',
        text: 'commons:coming_soon_tag',
        className: 'bg-icoPurple-100 dark:bg-icoPurple/20 text-icoPurple border-icoPurple',
        classNameShorten: 'bg-icoPurple text-white border-icoPurple',
        dataTestId: 'badge-coming-soon'
    },
    info: {
        icon: 'dot',
        text: 'commons:info_tag',
        className: 'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue border-icoBlue',
        classNameShorten: 'bg-icoBlue text-white border-icoBlue',
        dataTestId: 'badge-info'
    },
    error: {
        icon: 'rejected',
        text: 'commons:error_tag',
        className: 'bg-icoRed-100 dark:bg-icoRed/20 text-icoRed border-icoRed',
        classNameShorten: 'bg-icoRed text-white border-icoRed',
        dataTestId: 'badge-error'
    },
    important: {
        icon: 'dot',
        text: 'commons:important_tag',
        className: 'bg-icoOrange-100 dark:bg-icoOrange/20 text-icoOrange border-icoOrange',
        classNameShorten: 'bg-icoOrange text-white border-icoOrange',
        dataTestId: 'badge-important'
    },
    new: {
        icon: 'sparkles',
        text: 'commons:new_tag',
        className: 'bg-icoBlue-100 dark:bg-icoBlue/20 text-icoBlue border-icoBlue',
        classNameShorten: 'bg-icoBlue text-white border-icoBlue',
        dataTestId: 'badge-new'
    },
    newDarken: {
        icon: 'sparkles',
        text: 'commons:new_tag',
        className: 'bg-icoBlue-100 dark:bg-icoBlue text-icoBlue dark:text-white border-icoBlue',
        classNameShorten: 'bg-icoBlue text-white border-icoBlue',
        dataTestId: 'badge-new'
    },
    update: {
        icon: 'dot',
        text: 'commons:update',
        className: 'bg-icoAquaBlue-100 dark:bg-icoAquaBlue/20 text-icoAquaBlue border-icoAquaBlue',
        classNameShorten: 'bg-icoAquaBlue text-white border-icoAquaBlue',
        dataTestId: 'badge-update'
    },
    upgrade: {
        icon: 'crown',
        text: 'commons:upgrade',
        className: 'bg-icoGreen-100 dark:bg-icoGreen/20 text-icoGreen border-icoGreen',
        classNameShorten: 'bg-icoGreen text-white border-icoGreen',
        dataTestId: 'badge-upgrade'
    },
    deprecated: {
        ...deprecatedConfigVariant,
        text: 'commons:deprecated_tag',
        dataTestId: 'badge-deprecated'
    },
    network: {
        icon: '',
        text: 'commons:network',
        className:
            'rounded-sm bg-icoAquaBlue-100 border-none font-GSemiBold px-1.5 py-1.5 dark:bg-icoDarkMode-anthracite text-icoAquaBlue dark:text-icoDarkMode-wolf',
        classNameShorten:
            'bg-icoGray-100 bg-icoAquaBlue-100 text-icoAquaBlue-600 border-icoGray-300 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf',
        dataTestId: 'badge-network'
    },
    tip: {
        icon: 'inspiration-outline',
        text: 'commons:tip',
        className: 'bg-icoGreen-100 dark:bg-icoGreen/20 text-icoGreen border-icoGreen',
        classNameShorten: 'bg-icoGreen text-white border-icoGreen',
        dataTestId: 'badge-tip'
    },
    'deprecated-organic': {
        ...deprecatedConfigVariant,
        text: 'commons:deprecated_organic_tag',
        dataTestId: 'badge-deprecated-organic'
    },
    'deprecated-paid': {
        ...deprecatedConfigVariant,
        text: 'commons:deprecated_paid_tag',
        dataTestId: 'badge-deprecated-paid'
    },
    rejected: {
        icon: '',
        text: 'scheduler:status.rejected',
        className:
            'flex items-center justify-center gap-1 rounded-sm border border-icoRed-200 px-1.5 py-1.5 text-icoRed',
        classNameShorten: 'bg-icoRed text-white border-icoRed/20 rounded-sm px-1.5 py-0.5',
        dataTestId: 'badge-rejected'
    },
    approved: {
        icon: '',
        text: 'scheduler:status.approved',
        className:
            'flex items-center justify-center gap-1 rounded-sm border border-icoBlue-200 px-1.5 py-1.5 text-icoBlue',
        classNameShorten: 'bg-icoBlue text-white border-icoBlue/20 rounded-sm px-1.5 py-0.5',
        dataTestId: 'badge-approved'
    },
    waiting_for_approval: {
        icon: '',
        text: 'scheduler:status.waiting_for_approval',
        className:
            'flex items-center justify-center gap-1 rounded-sm border border-icoPurple-200 px-1.5 py-1.5 text-icoPurple',
        classNameShorten: 'bg-icoPurple text-white border-icoPurple/50 rounded-sm px-1.5 py-0.5',
        dataTestId: 'badge-waiting_for_approval'
    },
    scheduled: {
        icon: '',
        text: 'scheduler:status.scheduled',
        className:
            'flex items-center justify-center gap-1 rounded-sm border border-icoBlue-200 px-1.5 py-1.5 text-icoBlue',
        classNameShorten: 'bg-icoBlue text-white border-icoBlue/20 rounded-sm px-1.5 py-0.5',
        dataTestId: 'badge-scheduled'
    },
    social_media: {
        icon: '',
        text: '',
        className:
            'flex items-center justify-center gap-1 rounded-sm bg-icoGray-300 px-1.5 py-1.5 text-icoGray-500 font-GSemiBold text-sm',
        classNameShorten: 'bg-icoGray-300 text-icoGray-500 rounded-sm px-1.5 py-0.5',
        dataTestId: 'badge-socialmedia'
    }
} as const;

type BadgeProps = {
    variant: keyof typeof variantConfig;
    children?: React.ReactNode;
} & (
    | {
          iconSize?: 'base';
          shorten?: false;
      }
    | {
          iconSize?: 'base' | 'sm';
          shorten: true;
      }
);

const Badge = ({ variant, shorten, iconSize = 'base', children }: BadgeProps) => {
    const { t } = useTranslation();
    const config = variantConfig[variant];

    return (
        <span
            data-testid={config.dataTestId}
            className={classNames('flex w-max items-center space-x-0.75 border', {
                'rounded-full px-1 py-0.5': !shorten,
                'rounded-m p-0.5': shorten,
                [config.className]: !shorten,
                [config.classNameShorten]: shorten
            })}>
            {config.icon && (
                <Icon
                    icon={config.icon}
                    className={classNames({
                        'text-xxs': iconSize === 'base' || !shorten,
                        'text-7': shorten && iconSize === 'sm'
                    })}
                />
            )}
            {!shorten ? (
                <span className="h-[7px] text-8 uppercase leading-none tracking-0.5">
                    {children || t(config.text)}
                </span>
            ) : null}
        </span>
    );
};

export default Badge;
